"use client";

import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import styles from './_gc-conversion-step.module.scss';
import { FormInputCheckbox, FormInputDropdown, FormInputText } from '@/components/form';
import Button from '@/components/button';
import {
  GC_CONVERSION_ITEMS,
  GC_CONVERSION_PATIENT_TYPE,
  computeGcConversion,
} from '@/data/gc-conversion-calculator-data';

const FOOTNOTE = {
  [GC_CONVERSION_PATIENT_TYPE.PEDIATRIC]:
    'This calculator is designed using hydrocortisone equivalents; the conversion factors for potency: 5x for prednisolone and prednisone, 6x for methylprednisolone, and ~60x for dexamethasone.',
  [GC_CONVERSION_PATIENT_TYPE.ADULT]:
    'This calculator is designed using hydrocortisone equivalents; the conversion factors for potency: 4x for prednisolone and prednisone, 5x for methylprednisolone, and ~60x for dexamethasone.',
};

/**
 * @description GC Conversion Calculator — Step: Patient Inputs + Result
 * After first Calculate, result appears inline and auto-updates on any field change.
 * Figma nodes: 14753:30230 (input), 14753:29732 (pediatric result), 15104:75323 (adult result)
 *
 * @param {Function} onNext   — called with result object on each calculation
 * @param {Function} onReset  — called to return to the intro screen
 */
function GcConversionStep({ onNext, onReset }) {
  const [patientType,    setPatientType]    = useState(null);
  const [convertFrom,    setConvertFrom]    = useState(null);
  const [dose,           setDose]           = useState('');
  const [convertTo,      setConvertTo]      = useState(null);
  const [result,         setResult]         = useState(null);
  const [hasCalculated,  setHasCalculated]  = useState(false);

  const convertFromRef = useRef(null);
  const convertToRef   = useRef(null);
  const doseRef        = useRef(null);

  const allFieldsValid = useMemo(() => {
    const doseNum = parseFloat(dose);
    return !!(patientType && convertFrom && convertTo && !isNaN(doseNum) && doseNum > 0);
  }, [patientType, convertFrom, convertTo, dose]);

  // Auto-recalculate whenever any field changes after the first calculation
  useEffect(() => {
    if (!hasCalculated) return;
    if (allFieldsValid) {
      const computed = computeGcConversion(convertFrom, dose, convertTo, patientType);
      setResult(computed);
      onNext?.({ patientType, convertFrom, dose, convertTo, result: computed });
    } else {
      setResult(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientType, convertFrom, convertTo, dose, hasCalculated]);

  const handleCalculate = useCallback(() => {
    const computed = computeGcConversion(convertFrom, dose, convertTo, patientType);
    setResult(computed);
    setHasCalculated(true);
    onNext?.({ patientType, convertFrom, dose, convertTo, result: computed });
  }, [convertFrom, convertTo, dose, patientType, onNext]);

  const fromLabel = GC_CONVERSION_ITEMS.find((i) => i.code === convertFrom)?.labelText ?? '';
  const toLabel   = GC_CONVERSION_ITEMS.find((i) => i.code === convertTo)?.labelText ?? '';

  return (
    <>
      <div className={styles.form_card}>

        {/* ── Card header ──────────────────────────────────────────────── */}
        <div className={styles.card_header}>
          <h3 className={styles.card_title}>GC Conversion Calculator</h3>
        </div>

        {/* ── Disclaimer ───────────────────────────────────────────────── */}
        <p className={styles.disclaimer}>
          GC dose conversions for cortisol replacement in classic CAH are not
          equivalent to GC dose conversions in other therapeutic areas, such as
          immune diseases.
        </p>

        {/* ── Form body ────────────────────────────────────────────────── */}
        <div className={styles.form_body}>
          <p className={styles.section_title}>Patient&rsquo;s GC dose</p>

          {/* ── Patient type ─────────────────────────────────────────── */}
          <fieldset className={styles.field_group}>
            <legend className={styles.field_label}>Please select one</legend>
            <div className={styles.radio_row}>
              <FormInputCheckbox
                name="gc-conversion-patient-pediatric"
                typeRadio
                radioGroupName="gc-conversion-patient-type"
                onChange={() => setPatientType(GC_CONVERSION_PATIENT_TYPE.PEDIATRIC)}
                data-gtm-event-category="gc-conversion-calculator"
                data-gtm-event-action="select"
                data-gtm-event-label="Pediatric patient"
              >
                Pediatric patient
              </FormInputCheckbox>
              <FormInputCheckbox
                name="gc-conversion-patient-adult"
                typeRadio
                radioGroupName="gc-conversion-patient-type"
                onChange={() => setPatientType(GC_CONVERSION_PATIENT_TYPE.ADULT)}
                data-gtm-event-category="gc-conversion-calculator"
                data-gtm-event-action="select"
                data-gtm-event-label="Adult patient"
              >
                Adult patient
              </FormInputCheckbox>
            </div>
          </fieldset>

          {/* ── Convert from ─────────────────────────────────────────── */}
          <div className={styles.field_group}>
            <div className={styles.convert_row}>
              <div className={styles.dropdown_wrap}>
                <FormInputDropdown
                  ref={convertFromRef}
                  name="gc-conversion-from"
                  label="Convert from"
                  placeholder="Select one"
                  itemList={GC_CONVERSION_ITEMS}
                  onChange={(item) => setConvertFrom(item?.code ?? null)}
                  data-gtm-event-category="gc-conversion-calculator"
                  data-gtm-event-action="select"
                  data-gtm-event-label="Convert from"
                  omitFirstItem
                  disableFirstItem={false}
                />
              </div>
              <div className={styles.dose_field}>
                <div className={styles.dose_input}>
                  <FormInputText
                    ref={doseRef}
                    name="gc-conversion-dose"
                    label="Dose"
                    ariaLabel="Dose in mg"
                    placeholder="Eg, 30"
                    inputMode="decimal"
                    autoformatDecimalOnly
                    onChange={(e) => setDose(e.target.value)}
                    data-gtm-event-category="gc-conversion-calculator"
                    data-gtm-event-action="input"
                    data-gtm-event-label="Dose"
                    maxLength={6}
                  />
                </div>
                <span className={styles.unit_label}>mg</span>
              </div>
            </div>
          </div>

          {/* ── Convert to ───────────────────────────────────────────── */}
          <div className={`${styles.field_group} ${styles.convert_row}`}>
            <FormInputDropdown
              ref={convertToRef}
              name="gc-conversion-to"
              label="Convert to"
              placeholder="Select one"
              itemList={GC_CONVERSION_ITEMS}
              onChange={(item) => setConvertTo(item?.code ?? null)}
              data-gtm-event-category="gc-conversion-calculator"
              data-gtm-event-action="select"
              data-gtm-event-label="Convert to"
              omitFirstItem
              disableFirstItem={false}
            />
            <div className="hidden tablet-down:block" />
          </div>
        </div>

        {/* ── Result box — shown after first calculation ────────────────── */}
        {hasCalculated && result !== null && (
          <div className={styles.result_box}>
            <span className={styles.result_label}>An approximate GC dose conversion:</span>
            <div className={styles.result_equation_wrap}>
              <p className={styles.result_equation}>
                {fromLabel}&nbsp;{dose}&nbsp;mg = {toLabel}&nbsp;{result}&nbsp;mg
              </p>
            </div>
          </div>
        )}

        {/* ── Footnote — shown after first calculation ──────────────────── */}
        {hasCalculated && patientType && (
          <p className={styles.footnote}>{FOOTNOTE[patientType]}</p>
        )}

        {/* ── Calculate button — hidden after first calculation ─────────── */}
        {!hasCalculated && (
          <div className={styles.button_row}>
            <Button
              iconType="arrow-right"
              type="button"
              variant={['solid', 'solid--eggplant', 'hcp-big']}
              disabled={!allFieldsValid}
              onClick={handleCalculate}
              data-gtm-event-category="gc-conversion-calculator"
              data-gtm-event-action="click"
              data-gtm-event-label="Calculate"
            >
              Calculate
            </Button>
          </div>
        )}

      </div>
    </>
  );
}

export default GcConversionStep;
